import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import ProductTile from './ProductTile';
import { ProductGridSkeleton } from './AppSkeletons';
import { useI18n } from '../context/I18nContext';
import './ProductGrid.css';
import API_BASE_URL from '../config';

const isNative = () => Boolean(window.Capacitor?.isNativePlatform());

const formatCategoryLabel = (value) => {
  const raw = (value || '').toString().trim();
  if (!raw) return '';

  const withoutParenCode = raw.replace(/^\s*(\([^)]*\)\s*)+/, '');
  const withoutAcronymPrefix = withoutParenCode.replace(/^\s*[A-Z]{2,6}\s*[-:|]\s*/, '');
  return withoutAcronymPrefix.trim() || raw;
};

const pathKey = (path) => path.join(' > ');

const CATEGORY_ICONS = {
  mercearia: '🥫',
  bebidas: '🥤',
  fruta: '🍎',
  legumes: '🥦',
  carnes: '🥩',
  peixe: '🐟',
  lacticinios: '🥛',
  lactici: '🥛',
  congelados: '🧊',
  higiene: '🧼',
  limpeza: '🧹',
  padaria: '🍞',
  snacks: '🍿',
  batatas: '🥔',
  cereais: '🌾',
  animais: '🐾',
  bebes: '👶',
  cafe: '☕',
  bolachas: '🍪',
  chocolate: '🍫',
  azeite: '🫙',
  massas: '🍝',
  arroz: '🍚',
  iogurte: '🥛',
  queijo: '🧀',
};

const getCategoryIcon = (categoryLabel) => {
  const normalized = (categoryLabel || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  for (const [key, icon] of Object.entries(CATEGORY_ICONS)) {
    if (normalized.includes(key)) return icon;
  }
  return '🛒';
};

const parseSelectedPathFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const category = (params.get('category') || '').trim();
  const subcategories = (params.get('subcategories') || '')
    .split('|')
    .map((value) => value.trim())
    .filter(Boolean);

  return [category, ...subcategories].filter(Boolean);
};

const buildExpandedPaths = (selectedPath) => {
  const expanded = {};
  for (let i = 0; i < selectedPath.length; i += 1) {
    expanded[pathKey(selectedPath.slice(0, i + 1))] = true;
  }
  return expanded;
};

const ITEMS_PER_PAGE = 21;

const ProductGrid = ({ searchTerm, selectedCategory, resetSignal, openCategoryMenuSignal }) => {
  const { t } = useI18n();
  const location = useLocation();

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm || '');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPath, setSelectedPath] = useState(() => parseSelectedPathFromSearch(location.search));
  const [expandedPaths, setExpandedPaths] = useState(() => buildExpandedPaths(parseSelectedPathFromSearch(location.search)));
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('relevance');
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Pull-to-refresh (native only)
  const pullStartY = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const PULL_THRESHOLD = 72;

  useEffect(() => {
    if (!isNative()) return;

    const onTouchStart = (e) => {
      if (window.scrollY === 0) pullStartY.current = e.touches[0].clientY;
      else pullStartY.current = 0;
    };

    const onTouchMove = (e) => {
      if (!pullStartY.current || isRefreshing) return;
      const delta = e.touches[0].clientY - pullStartY.current;
      if (delta > 0 && window.scrollY === 0) {
        setPullDistance(Math.min(delta * 0.45, PULL_THRESHOLD + 20));
      }
    };

    const onTouchEnd = () => {
      if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
        setIsRefreshing(true);
        import('@capacitor/haptics').then(({ Haptics, ImpactStyle }) => {
          Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
        });
        setTimeout(() => {
          setRefreshSignal((s) => s + 1);
          setIsRefreshing(false);
          setPullDistance(0);
        }, 800);
      } else {
        setPullDistance(0);
      }
      pullStartY.current = 0;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullDistance, isRefreshing]);

  const setPath = (path) => {
    setSelectedPath(path);
  };

  useEffect(() => {
    setSelectedPath([]);
    setExpandedPaths({});
    setIsMobileFilterOpen(false);
    setCurrentPage(1);
  }, [resetSignal]);

  useEffect(() => {
    if (parseSelectedPathFromSearch(location.search).length) {
      return;
    }
    if (selectedCategory) {
      setSelectedPath([selectedCategory]);
      setExpandedPaths((prev) => ({ ...prev, ...buildExpandedPaths([selectedCategory]) }));
      setCurrentPage(1);
    }
  }, [selectedCategory, location.search]);

  useEffect(() => {
    const pathFromSearch = parseSelectedPathFromSearch(location.search);
    if (!pathFromSearch.length) {
      return;
    }

    setSelectedPath(pathFromSearch);
    setExpandedPaths((prev) => ({ ...prev, ...buildExpandedPaths(pathFromSearch) }));
    setCurrentPage(1);
  }, [location.search]);

  useEffect(() => {
    if (parseSelectedPathFromSearch(location.search).length) {
      return;
    }
    const pathFromState = location?.state?.selectedPath;
    if (!Array.isArray(pathFromState) || pathFromState.length === 0) return;

    const sanitizedPath = pathFromState
      .map((value) => (value || '').toString().trim())
      .filter(Boolean);

    if (!sanitizedPath.length) return;

    setSelectedPath(sanitizedPath);
    setExpandedPaths((prev) => ({ ...prev, ...buildExpandedPaths(sanitizedPath) }));
    setCurrentPage(1);
  }, [location, location.search]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [selectedCategory, location.key]);

  useEffect(() => {
    document.body.style.overflow = isMobileFilterOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileFilterOpen]);

  useEffect(() => {
    if (!openCategoryMenuSignal) return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      setIsMobileFilterOpen(true);
    }
  }, [openCategoryMenuSignal]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm || '');
    }, 220);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/products/categories`, { timeout: 12000 });
      return response.data?.tree || {};
    },
    staleTime: 5 * 60_000,
  });
  const categoryTree = categoriesData || {};

  const prevFiltersRef = useRef({ search: debouncedSearchTerm, path: selectedPath.join(','), sort: sortOrder });
  useEffect(() => {
    const { search, path, sort } = prevFiltersRef.current;
    const newPath = selectedPath.join(',');
    if (search !== debouncedSearchTerm || path !== newPath || sort !== sortOrder) {
      prevFiltersRef.current = { search: debouncedSearchTerm, path: newPath, sort: sortOrder };
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, selectedPath, sortOrder]);

  const [catalogCategory] = selectedPath;
  const catalogSubPath = selectedPath.slice(1);
  const { data: catalogData, isLoading: loading, isError } = useQuery({
    queryKey: ['product-catalog', currentPage, debouncedSearchTerm, selectedPath, sortOrder, refreshSignal],
    queryFn: async ({ signal }) => {
      const response = await axios.get(`${API_BASE_URL}/products/catalog`, {
        timeout: 12000,
        signal,
        params: {
          page: currentPage,
          page_size: ITEMS_PER_PAGE,
          search: debouncedSearchTerm || undefined,
          category: catalogCategory || undefined,
          subcategories: catalogSubPath.length ? catalogSubPath.join('|') : undefined,
          sort: sortOrder,
        },
      });
      return response.data;
    },
    placeholderData: (prev) => prev,
  });
  const products = Array.isArray(catalogData?.items) ? catalogData.items : [];
  const totalCount = Number(catalogData?.total_count) || 0;
  const totalPages = Number(catalogData?.total_pages) || 0;
  const error = isError ? t('productGrid.failedLoad') : null;

  if (loading) {
    return <ProductGridSkeleton />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const toggleExpanded = (path) => {
    const key = pathKey(path);
    setExpandedPaths((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderTree = (nodes, path = [], depth = 0) => {
    return (
      <ul className="category-tree">
        {Object.keys(nodes).map((nodeKey) => {
          if (nodeKey === '__children') return null;
          const nextPath = [...path, nodeKey];
          const nodePathKey = pathKey(nextPath);
          const isSelected = pathKey(selectedPath) === nodePathKey;
          const children = nodes[nodeKey].__children || {};
          const hasChildren = Object.keys(children).length > 0;
          const isExpanded = Boolean(expandedPaths[nodePathKey]);
          const isRoot = depth === 0;

          return (
            <li key={nodePathKey}>
              <div className={`tree-node-row depth-${depth}${isRoot ? ' root' : ''}`}>
                {!isRoot && (
                  hasChildren ? (
                    <button
                      type="button"
                      className={`tree-expand-btn${isExpanded ? ' expanded' : ''}`}
                      onClick={() => toggleExpanded(nextPath)}
                      aria-label={isExpanded ? t('productGrid.collapseSubcategories') : t('productGrid.expandSubcategories')}
                    >
                      ▸
                    </button>
                  ) : (
                    <span className="tree-expand-spacer" aria-hidden="true" />
                  )
                )}

                <button
                  type="button"
                  className={`tree-node${isSelected ? ' selected' : ''}${isRoot ? ' tree-node-root' : ' tree-node-sub'}`}
                  onClick={() => {
                    setPath(nextPath);
                    setIsMobileFilterOpen(false);
                  }}
                >
                  {isRoot && <span className="tree-node-icon" aria-hidden="true">{getCategoryIcon(nodeKey)}</span>}
                  <span className="tree-node-label">{formatCategoryLabel(nodeKey)}</span>
                </button>

                {isRoot && (
                  hasChildren ? (
                    <button
                      type="button"
                      className={`tree-expand-btn root-expand${isExpanded ? ' expanded' : ''}`}
                      onClick={() => toggleExpanded(nextPath)}
                      aria-label={isExpanded ? t('productGrid.collapseSubcategories') : t('productGrid.expandSubcategories')}
                    >
                      ▸
                    </button>
                  ) : (
                    <span className="tree-expand-spacer root-expand-spacer" aria-hidden="true" />
                  )
                )}
              </div>

              {hasChildren && isExpanded && renderTree(children, nextPath, depth + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const helmetCategory = selectedPath[0] ? formatCategoryLabel(selectedPath[0]) : null;

  return (
    <div className="product-grid-container">
      {isNative() && (pullDistance > 0 || isRefreshing) && (
        <div
          className={`pull-to-refresh${isRefreshing ? ' pull-to-refresh--refreshing' : ''}`}
          style={{ '--pull-distance': `${pullDistance}px` }}
          aria-hidden="true"
        >
          <div className="ptr-spinner" />
        </div>
      )}
      <Helmet>
        <title>{helmetCategory ? `${helmetCategory} — Preços | Poupix` : 'Produtos — Poupix'}</title>
        <meta name="description" content={helmetCategory ? `Compare preços de ${helmetCategory} nos principais supermercados portugueses.` : 'Comparador de preços de supermercado em Portugal.'} />
      </Helmet>
      <div className="product-grid-layout">
        <aside className="product-sidebar" role="region" aria-label={t('productGrid.categoryMenuAria')}>
          <div className="desktop-category-menu-head">
            <span className="desktop-category-title">{t('productGrid.filterByCategory')}</span>
          </div>
          <div className="desktop-category-menu">
            {renderTree(categoryTree)}
          </div>
        </aside>

        <section className="product-content-area">
          <div className="products-toolbar">
            <button
              type="button"
              className="mobile-open-filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              {t('productGrid.mobileOpenFilters')}
            </button>
            <div className="toolbar-right">
              <label className="sort-label" htmlFor="sort-select">{t('productGrid.sortBy')}</label>
              <select
                id="sort-select"
                className="sort-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="relevance">{t('productGrid.sortRelevance')}</option>
                <option value="price_asc">{t('productGrid.sortPriceAsc')}</option>
                <option value="price_desc">{t('productGrid.sortPriceDesc')}</option>
                <option value="savings_desc">{t('productGrid.sortSavings')}</option>
              </select>
              <span className="products-count">
                {t('common.productsCount', { count: totalCount })}
              </span>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="no-products">{t('common.noProductsFound')}</div>
          ) : (
            <>
              <div className="product-grid">
                {products.map(product => {
                  return (
                    <ProductTile
                      key={product.id}
                      product={product}
                      cheapestPrice={product.cheapest_price}
                      cheapestPricePerUnit={product.cheapest_price_per_unit}
                      cheapestImageUrl={product.cheapest_image_url}
                      supermarket={product.cheapest_supermarket}
                      supermarketId={product.cheapest_supermarket_id}
                    />
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="pagination" role="navigation" aria-label={t('productGrid.paginationAria')}>
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    {t('common.previous')}
                  </button>
                  <span className="page-status">{t('common.pageOf', { page: currentPage, total: totalPages })}</span>
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t('common.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {isMobileFilterOpen && (
        <div className="mobile-filter-overlay" onClick={() => setIsMobileFilterOpen(false)}>
          <aside className="mobile-filter-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-filter-head">
              <h3>{t('productGrid.filterByCategory')}</h3>
              <button
                type="button"
                className="mobile-filter-close"
                onClick={() => setIsMobileFilterOpen(false)}
                aria-label={t('productGrid.closeCategoryFilters')}
              >
                ✕
              </button>
            </div>

            {renderTree(categoryTree)}
          </aside>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
